import { Component, OnInit } from '@angular/core';
import { ApiUrlHelper } from '../../../../common/api-url-helper';
import { CommonService } from '../../../../services/common/common.service';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { StateService } from '../../../../services/common/claim-year.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Router } from '@angular/router';
import { commonLabelConstants, staffingCostByProject } from '../../../../constants/label-constants';
import { environment } from '../../../../../environments/environment';
import { CustomCurrencyPipe } from '../../../../common/pipes/custom-currency.pipe';
import { SharedService } from '../../../../services/shared-service/shared.service';
import { Subject, takeUntil } from 'rxjs';
import { HttpResponse } from '@angular/common/http';
import { ValidationService } from '../../../../services/common/validation-service';

@Component({
  selector: 'app-staff-cost-form',
  templateUrl: './staff-cost-form.component.html',
  styleUrls: ['./staff-cost-form.component.scss'],
})
export class StaffCostFormComponent implements OnInit {

  env: any = environment;
  projectList: any = [];
  companyId: any = 0;
  claimYearId: any = 0;
  staffRNDForm!: FormGroup;
  detail: any = [];
  isLoading: boolean = false;
  labelConstants = staffingCostByProject;
  commonLabelConstants = commonLabelConstants;
  onDestroy$: Subject<void> = new Subject();
  isLocked: boolean = false;

  constructor(
    private readonly api: ApiUrlHelper,
    private readonly commonService: CommonService,
    private readonly sharedDataService: StateService,
    private readonly fb: FormBuilder,
    private readonly spinner: NgxSpinnerService,
    private readonly router: Router,
    private readonly currencyPipe: CustomCurrencyPipe,
    private readonly sharedService: SharedService,
    private readonly validationService: ValidationService
  ) { }

  get employee(): FormArray {
    return this.staffRNDForm.get('employee') as FormArray;
  }

  ngOnInit() {
    this.staffRNDForm = this.fb.group({
      employee: this.fb.array([]),
    });
    this.sharedDataService.companyId$.subscribe((companyId) => {
      this.companyId = companyId;
    });
    this.sharedDataService.yearId$.pipe(takeUntil(this.onDestroy$)).subscribe((yearId) => {
      this.claimYearId = yearId ?? null;
      if (this.companyId && this.claimYearId) {
        this.resetForm();
        this.getProjectListCompanyWise(this.companyId, this.claimYearId)
      }
    });
  }

  resetForm() {
    this.staffRNDForm = this.fb.group({
      employee: this.fb.array([]),
    });
  }

  createEmployee(): FormGroup {
    return this.fb.group({
      employeeId: [null],
      employeeName: [{ value: '', disabled: true }, Validators.required],
      employeeJobRole: [{ value: '', disabled: true }, Validators.required],
      totalGross: [{ value: '', disabled: true }, Validators.required],
      rationaleForRNDEligibilityAssessment: [''],
      staffCostResourceId: [0],
      totalEligibilityPer: [{ value: 0, disabled: true }],
      project: this.fb.array([]),
    });
  }

  getProjectListCompanyWise(companyId: any, claimYearId: any) {
    this.spinner.show();
    let api = this.api.apiUrl.Project.GetProjectListWithOutPagination.replace(
      '{companyId}',
      companyId
    ).replace('{claimYearId}', claimYearId);
    this.commonService
      .doGet(api)
      .pipe()
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.projectList = response.data;
            this.getEmployeeDetail();
          } else {
            this.projectList = [];
          }
        },
        error: (error: any) => {
          console.log(error);
          this.spinner.hide();
        },
        complete: () => {
          this.spinner.hide();
        },
      });
  }

  getEmployeeDetail() {
    this.isLoading = true;
    let obj = {
      companyId: this.companyId,
      claimYearId: this.claimYearId,
    };

    let api = this.api.apiUrl.StaffCostResource.employeeList;

    this.commonService
      .doPost(api, obj)
      .pipe()
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.isLoading = false;
            this.detail = response.data;
            this.isLocked = ( this.detail[0]?.isLocked ?? false);
            this.detail.forEach((item: any) => {
              const newEmployee = this.createEmployee();
              newEmployee.patchValue({
                employeeId: item.employeeId,
                employeeName: item.employeeName,
                employeeJobRole: item.role,
                totalGross: item.totalGross,
                rationaleForRNDEligibilityAssessment:
                  item.rationaleForRNDEligibilityAssessment,
                totalEligibilityPer: item.totalEligibilityAmount,
                staffCostResourceId: item.staffCostResourceId,
              });

              const employeeDetailsArray = newEmployee.get(
                'project'
              ) as FormArray;

              this.projectList.forEach((itemDetail: any) => {
                const newProjectDetail = this.createEmployeeProjectDetail();

                const projectData = item.projects.find(
                  (projectDetail: any) =>
                    projectDetail.projectId == itemDetail.projectId
                );

                const rndEligibilityValue = projectData?.rndEligibility ?? '0';

                newProjectDetail.patchValue({
                  staffCostResourceEligibilityId: projectData
                    ? projectData.staffCostResourceEligibilityId
                    : 0,
                  staffCostResourceId: projectData
                    ? projectData.staffCostResourceId
                    : 0,
                  projectId: itemDetail.projectId,
                  rndEligibility: rndEligibilityValue,
                });

                newProjectDetail.disable();

                employeeDetailsArray.push(newProjectDetail);
              });

              this.employee.push(newEmployee);
            });

            this.spinner.hide();
          }
        },
        error: (error: any) => {
          this.isLoading = false;
          console.log(error);
          this.spinner.hide();
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  createEmployeeProjectDetail() {
    return this.fb.group({
      staffCostResourceEligibilityId: [null],
      staffCostResourceId: [null],
      projectId: ['', Validators.required],
      rndEligibility: [''],
    });
  }

  getItemAtIndex(index: number): FormGroup {
    return this.employee.at(index) as FormGroup;
  }

  getProjectDetails(index: any) {
    return this.getItemAtIndex(index).get('project') as FormArray;
  }

  getTotalValueOfEmployee(index: any) {
    let total = 0;
    for (let i = 0; i < this.getProjectDetails(index).length; i++) {
      const detail = this.getProjectDetails(index).at(i).value;

      total += parseFloat(detail.rndEligibility ? detail.rndEligibility : 0);
    }
    return total;
  }

  goBack(): void {
    this.router.navigateByUrl('/dashboard');
  }

  getCostTotal(): number {
    return this.employee.controls.reduce((totalCost, contractor) => {
      const cost = contractor.get('totalGross')?.value || 0;
      return totalCost + Number(cost);
    }, 0);
  }

  getTotalProduct(projectIndex: number): string | null {
    let totalProduct = 0;

    for (let i = 0; i < this.employee.length; i++) {
      const employee = this.getItemAtIndex(i);
      const projectDetails = this.getProjectDetails(i);

      if (projectIndex < projectDetails.length) {
        const totalGrossStr = employee.get('totalGross')?.value;
        const totalGross = parseFloat(totalGrossStr) || 0;

        const rndEligibilityValueStr = projectDetails.at(projectIndex).get('rndEligibility')?.value;
        const rndEligibility = (parseFloat(rndEligibilityValueStr) || 0) / 100;

        totalProduct += totalGross * rndEligibility;
      } else {
        console.warn(`Project index ${projectIndex} is out of bounds for employee ${i}.`);
      }
    }
    return this.currencyPipe.transform(totalProduct === 0 ? 0 : Number(totalProduct.toFixed(2)), environment.CurrencyFormat);
  }

  getTotalQualifyingCosts(): number {
    let total = 0;
    const employeeArray = this.staffRNDForm.get('employee') as FormArray;
    employeeArray.controls.forEach((employee) => {
      total += employee.get('totalEligibilityPer')?.value || 0;
    });

    return total;
  }
  ngOnDestroy() {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  exportList(): void {
    const isValid = this.validationService.validateCompanyAndYear(
      this.companyId,
      this.claimYearId
    );
    if (!isValid) {
      return;
    }
    const companyIdStr = this.companyId !== null ? String(this.companyId) : '';
    const yearIdStr = this.claimYearId !== null ? String(this.claimYearId) : '';
    let obj = {
      CompanyId: companyIdStr,
      ClaimYearId: yearIdStr,
    };
    let api = this.api.apiUrl.StaffCostResource.costCategoryExport;
    this.commonService.doDownloadPost(api, obj).subscribe(
      (response: HttpResponse<Blob>) => {
        const blob = response.body as Blob;
        const link = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.download = "Staff - Qualifying Expenditure by Cost Category.xlsx";

        document.body.appendChild(link);
        link.click();

        window.URL.revokeObjectURL(url);
        document.body.removeChild(link);
      },
      (error: any) => {
        console.log(error);
      }
    );
  }
}
